import React, { useState, useEffect } from 'react';
import styles from '../styles';
import CtaButton from '../components/CtaButton';

const InfoBlock = ({ label, children }) => (
  <div style={{ borderTop: '2px solid rgba(27, 43, 62, 0.1)', paddingTop: '32px' }}>
    <span style={styles.infoLabel}>{label}</span>
    {children}
  </div>
);

const ContactPage = () => {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    creditScore: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Enter a valid email';
    if (!formData.message.trim()) newErrors.message = 'Please tell us about your situation';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitted(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const inputStyle = (field) => ({
    width: '100%',
    padding: '16px 0',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `2px solid ${errors[field] ? '#ef4444' : '#1B2B3E'}`,
    fontFamily: "'Inter', sans-serif",
    fontSize: '16px',
    color: '#1B2B3E',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  });

  return (
    <div>
      <header
        style={{
          paddingTop: '120px',
          paddingBottom: '64px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition:
            'opacity 1s cubic-bezier(0.2, 0.8, 0.2, 1), transform 1s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        <div style={styles.container}>
          <h1 style={styles.heroTitleUpper}>
            Let's Talk<br />Credit.
          </h1>
          <p style={styles.heroSub}>
            Your free consultation is one message away. We respond to every inquiry within one
            business day.
          </p>
        </div>
      </header>

      <section style={{ ...styles.container, paddingBottom: '180px' }}>
        {submitted ? (
          <div
            style={{
              borderTop: '2px solid #1B2B3E',
              paddingTop: '64px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '24px',
            }}
          >
            <div
              style={{
                fontFamily: "'Antonio', sans-serif",
                fontSize: '56px',
                color: '#1B2B3E',
                textTransform: 'uppercase',
                letterSpacing: '-1px',
                lineHeight: '0.95',
              }}
            >
              Message<br />Received.
            </div>
            <p
              style={{
                fontSize: '18px',
                color: '#475569',
                maxWidth: '500px',
                lineHeight: '1.6',
              }}
            >
              Thank you, {formData.name}. We've received your inquiry and will be in touch within
              one business day to schedule your free consultation.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({ name: '', email: '', phone: '', message: '', creditScore: '' });
              }}
              style={{
                backgroundColor: '#1B2B3E',
                color: '#F3F4F6',
                border: 'none',
                padding: '16px 32px',
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: '2px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginTop: '16px',
              }}
            >
              Send Another Message
            </button>
          </div>
        ) : (
          <div
            className="contact-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '120px',
              marginTop: '64px',
            }}
          >
            {/* Form */}
            <form
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}
            >
              <div>
                <label style={{ ...styles.detailLabel, marginBottom: '8px' }}>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Smith"
                  style={inputStyle('name')}
                />
                {errors.name && (
                  <span
                    style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', display: 'block' }}
                  >
                    {errors.name}
                  </span>
                )}
              </div>
              <div>
                <label style={{ ...styles.detailLabel, marginBottom: '8px' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@example.com"
                  style={inputStyle('email')}
                />
                {errors.email && (
                  <span
                    style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', display: 'block' }}
                  >
                    {errors.email}
                  </span>
                )}
              </div>
              <div>
                <label style={{ ...styles.detailLabel, marginBottom: '8px' }}>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 000-0000"
                  style={inputStyle('phone')}
                />
              </div>
              <div>
                <label style={{ ...styles.detailLabel, marginBottom: '8px' }}>
                  Estimated Credit Score
                </label>
                <select
                  value={formData.creditScore}
                  onChange={(e) => handleChange('creditScore', e.target.value)}
                  style={{ ...inputStyle('creditScore'), appearance: 'none', cursor: 'pointer' }}
                >
                  <option value="">Select a range</option>
                  <option value="below-580">Below 580 (Poor)</option>
                  <option value="580-669">580-669 (Fair)</option>
                  <option value="670-739">670-739 (Good)</option>
                  <option value="740-799">740-799 (Very Good)</option>
                  <option value="800+">800+ (Exceptional)</option>
                </select>
              </div>
              <div>
                <label style={{ ...styles.detailLabel, marginBottom: '8px' }}>
                  Tell Us About Your Situation *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Describe your credit challenges and goals..."
                  rows={5}
                  style={{ ...inputStyle('message'), resize: 'vertical', paddingTop: '12px' }}
                />
                {errors.message && (
                  <span
                    style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', display: 'block' }}
                  >
                    {errors.message}
                  </span>
                )}
              </div>
              <button
                type="submit"
                style={{
                  backgroundColor: '#1B2B3E',
                  color: '#F3F4F6',
                  border: 'none',
                  padding: '20px 40px',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: '2px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  alignSelf: 'flex-start',
                  transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                Send Message
              </button>
            </form>

            {/* Sidebar */}
            <div>
              {/* Office Info */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '48px',
                  marginBottom: '64px',
                  animation: 'fadeInUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) 0.1s forwards',
                  opacity: 0,
                }}
              >
                <InfoBlock label="Office">
                  <span style={styles.infoDetail}>
                    4193 Flat Rock Dr<br />
                    Bldg 200<br />
                    Riverside, CA 92505
                  </span>
                </InfoBlock>

                <InfoBlock label="Connect">
                  <a href="tel:8882712293" style={styles.infoDetail}>
                    888.271.2293
                  </a>
                  <a
                    href="mailto:fcrcreditsolutions@gmail.com"
                    style={{ ...styles.infoDetail, fontSize: '24px', marginTop: '10px' }}
                  >
                    fcrcreditsolutions@gmail.com
                  </a>
                </InfoBlock>

                <InfoBlock label="Hours">
                  <span style={styles.infoDetail}>Mon — Fri: 9am - 6pm EST</span>
                  <span style={{ ...styles.infoDetail, fontSize: '18px', color: '#475569' }}>
                    Sat — Sun: Closed
                  </span>
                </InfoBlock>
              </div>

              {/* What Happens Next */}
              <div style={{ borderTop: '2px solid #1B2B3E', paddingTop: '32px' }}>
                <h3
                  style={{
                    fontFamily: "'Antonio', sans-serif",
                    fontSize: '32px',
                    color: '#1B2B3E',
                    textTransform: 'uppercase',
                    marginBottom: '16px',
                    letterSpacing: '-0.5px',
                  }}
                >
                  What Happens Next
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {[
                    { step: '01', text: 'We review your inquiry and credit situation.' },
                    { step: '02', text: 'A specialist reaches out to schedule your call.' },
                    { step: '03', text: 'Free 15-minute consultation to assess your case.' },
                    { step: '04', text: 'Receive your custom credit repair strategy.' },
                  ].map((item) => (
                    <div
                      key={item.step}
                      style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}
                    >
                      <span
                        style={{
                          fontFamily: "'Antonio', sans-serif",
                          fontSize: '20px',
                          color: 'rgba(27,43,62,0.25)',
                          minWidth: '32px',
                          lineHeight: '1.4',
                        }}
                      >
                        {item.step}
                      </span>
                      <span style={{ fontSize: '15px', color: '#475569', lineHeight: '1.6' }}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Consultation Booking CTA */}
      <section
        style={{
          backgroundColor: '#1B2B3E',
          color: '#F3F4F6',
          padding: '120px 0',
        }}
      >
        <div style={styles.container}>
          <div
            className="booking-inner"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            <div>
              <h2 style={styles.bookingTitle}>
                Direct<br />Consultation.
              </h2>
              <p style={styles.bookingSub}>
                Book a 15-minute strategy session with our senior credit analysts to map out your
                restoration path.
              </p>
            </div>
            <CtaButton text="Select Time Slot" light />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
